import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { API_BASE } from '../utils/apiBase';
import { setAuthSession } from '../utils/authSession';
import logo from '../assets/images/Al-burak-logo.png';
import './signin.css';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      toast.error('Your session has expired. Please sign in again.');
      window.history.replaceState({}, '', '/login');
    }

    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData((prev) => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: true,
      }));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/v1/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Login failed');
      }

      if (formData.rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email.trim().toLowerCase());
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      setAuthSession(result.user, result.token);
      window.location.reload();
    } catch (error) {
      const errorMsg = error.message || 'An error occurred during login. Please try again.';
      setApiError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: { background: '#111827', color: '#fff', borderRadius: '10px' },
        }}
      />

      <aside className="login-brand" aria-hidden="true">
        <div className="login-brand-content">
          <img src={logo} alt="Al-Burak Scents" className="login-brand-logo" />
          <h2 className="login-brand-title">Al-Burak Admin</h2>
          <p className="login-brand-subtitle">
            Manage your store, orders, inventory, and affiliates from one secure dashboard.
          </p>
          <div className="login-brand-features">
            {[
              'Real-time order tracking',
              'Product & inventory management',
              'Analytics & affiliate insights',
            ].map((text) => (
              <div key={text} className="login-brand-feature">
                <span className="login-brand-feature-dot" />
                {text}
              </div>
            ))}
          </div>
        </div>
      </aside>

      <main className="login-form-panel">
        <div className="login-form-card">
          <img src={logo} alt="" className="login-mobile-logo" />

          <header className="login-form-header">
            <h1>Welcome back</h1>
            <p>Sign in to your admin account to continue</p>
          </header>

          {apiError && <div className="login-api-error" role="alert">{apiError}</div>}

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                name="email"
                className={`login-input${errors.email ? ' error' : ''}`}
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="email"
                autoCorrect="off"
                spellCheck={false}
              />
              {errors.email && <span className="login-error-text">{errors.email}</span>}
            </div>

            <div className="login-field">
              <label htmlFor="password">Password</label>
              <div className="login-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className={`login-input has-toggle${errors.password ? ' error' : ''}`}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="current-password"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className="login-toggle-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
              {errors.password && <span className="login-error-text">{errors.password}</span>}
            </div>

            <div className="login-options">
              <label className="login-remember" htmlFor="rememberMe">
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                />
                <span>Remember me</span>
              </label>
            </div>

            <button type="submit" className="login-submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="login-spinner" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <footer className="login-footer">
            &copy; {new Date().getFullYear()} Al-Burak Scents. Admin access only.
          </footer>
        </div>
      </main>
    </div>
  );
}
